import { useState, useRef, useEffect } from 'preact/hooks';
import { Fragment } from 'preact';
import getProductType from '../../utils/getProductType';
import { DayPicker, useDayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import dayjs from 'dayjs';
import { Text, withText } from 'preact-i18n';
import Modal from '../Modal';
import DatesControls from '../DatePicker/components/DatesControls';
import stringToDate from '../../utils/stringToDate';
import { selectIcon } from '../../templates/place';
import flexibleCalendarImg from '../../assets/images/flexible-calendar.webp';
import flexibleCalendarFlatImg from '../../assets/images/flexible-calendar-flat.webp';
import flexibleCalendarIcon from '../../assets/images/ic-flexible-calendar.webp';
import mixpanelTracker from '../../utils/mixpanelTracker';
import differenceInDays from 'date-fns/differenceInDays';

const CALENDAR_EVENT = 'Calendar Button Clicked';
const TAB_TO_SECTION = {
  oneWay: 'single',
  round: 'rounded',
  openTicket: 'open',
};
const CALENDAR_CONTINUE_EVENT = 'Calendar Continue Clicked';

const ReturnOpenSwitch = ({ returnIsOpen, handleToggleOpenReturn, flatVariant }) => {
  const switchIconSrc = flatVariant ? flexibleCalendarFlatImg : flexibleCalendarIcon;
  return (
    <label className="udp-open-return">
      <img src={switchIconSrc} className="udp-open-return__icon" alt="" aria-hidden="true" />
      <div className="udp-open-return__text">
        <span className="udp-open-return__title">
          <Text id="label.open_return" />
        </span>
        <span className="udp-open-return__subtitle">
          <Text id="label.open_return_subtitle" />
        </span>
      </div>
      <div
        className={`udp-open-return__switch${returnIsOpen ? ' udp-open-return__switch--on' : ''}`}
        role="switch"
        aria-checked={returnIsOpen}
        tabIndex={0}
        onClick={handleToggleOpenReturn}
        onKeyDown={e => (e.key === ' ' || e.key === 'Enter') && handleToggleOpenReturn()}
      >
        <div className="udp-open-return__knob" />
      </div>
    </label>
  );
};

/**
 * Custom month caption: renders  ‹  MMMM YYYY  ›  in a single row.
 * Defined outside the component so its identity is stable across re-renders.
 */
const MonthCaption = ({ calendarMonth }) => {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();
  return (
    <div className="udp-calendar-header">
      <button
        type="button"
        className="udp-nav-btn"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        disabled={!previousMonth}
        aria-label="previous month"
      >
        ‹
      </button>
      <span className="udp-month-label">{dayjs(calendarMonth.date).format('MMMM YYYY')}</span>
      <button
        type="button"
        className="udp-nav-btn"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        disabled={!nextMonth}
        aria-label="next month"
      >
        ›
      </button>
    </div>
  );
};

// Hide the default nav — navigation is rendered inside MonthCaption
const HiddenNav = () => null;

/**
 * Custom DayButton for react-day-picker. When `badgeText` is set it overlays a
 * small label on the selected day for outbound/return in round-trip mode when
 * one leg is an open ticket.
 * When `showTodayAccentHint` is true, today's cell uses accent colour + icon
 * (unless today is the outbound or return endpoint of the selected range).
 */
const DayButtonWithBadge = ({
  modifiers,
  children,
  badgeText,
  showTodayAccentHint,
  className,
  ...buttonProps
}) => {
  const isTodayAccentHint =
    showTodayAccentHint && modifiers.today && !modifiers.outside;

  const mergedClassName = [className, isTodayAccentHint ? 'udp-day_button--today-hint' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <button {...buttonProps} className={mergedClassName}>
      {children}
      {modifiers.selected && badgeText && <span className="udp-day-badge">{badgeText}</span>}
    </button>
  );
};

/**
 * Unified date picker that replaces the two separate Datepicker components when the
 * `unifiedDatePicker` feature is active.
 */
const UnifiedDatePicker = ({
  departureDate,
  returnDate,
  returnPlaceholder,
  tripType,
  dateFormat,
  maxDaysSearch,
  openTicketText,
  showOpenTicket,
  flatVariant,
  error,
  showMundialButton,
  showTomorrowWithMundial,
  locale,
  onTripTypeChange,
  onDepartureChange,
  onReturnChange,
  onReturnOpenTicket,
  onDepartureOpenTicket,
  onFocus,
  onContinue,
  departureBadge,
  openTicketLabel,
  openCalendar,
  buttonBg,
  hasPassengersModal,
}) => {
  const compactUsedRef = useRef(false);
  const wrapperRef = useRef(null);
  const pendingContinueRef = useRef(false);
  const monthNavigationUsedRef = useRef(false);
  const tabChangesCountRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [modalTab, setModalTab] = useState('oneWay');
  // Internal Date objects for the inline calendar
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const toDateObj = str => (str ? stringToDate(str, dateFormat) : null);
  const toStr = date => (date ? dayjs(date).format(dateFormat.toUpperCase()) : '');
  const atDayStart = d => (d ? dayjs(d).startOf('day').toDate() : null);

  // Close modal when clicking outside the wrapper (desktop only)
  useEffect(() => {
    if (!isOpen || getProductType() === 'web-mobile') return;

    const handleOutsideClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeModal();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Sync calendar selection when external dates change while modal is open.
  // With no departure: no default selection (today is only highlighted in One-way).
  useEffect(() => {
    if (!isOpen) return;
    if (departureDate === openTicketText) {
      setStartDate(null);
    } else if (departureDate) {
      setStartDate(atDayStart(toDateObj(departureDate)));
    } else {
      setStartDate(null);
    }
    setEndDate(
      returnDate && returnDate !== openTicketText ? atDayStart(toDateObj(returnDate)) : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate, returnDate]);

  useEffect(() => {
    if (!openCalendar) return;
    openModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCalendar]);

  // Deferred onContinue: fires once departureDate propagates after handleContinue sets it
  useEffect(() => {
    if (!pendingContinueRef.current || !departureDate) return;
    pendingContinueRef.current = false;
    if (onContinue) onContinue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate]);

  // ─── Tab state ─────────────────────────────────────────────────────────────

  const deriveTab = () => {
    if (tripType === 'openTicket') return 'openTicket';
    // Open departure uses the Round tab + single-day picker for the concrete leg (return by default).
    if (departureDate === openTicketText) return 'round';
    if (tripType === 'round' || returnDate) return 'round';
    return 'oneWay';
  };

  // Currently displayed month (controlled navigation)
  const [currentMonth, setCurrentMonth] = useState(null);

  // ─── Calendar bounds & suggested return (toDateObj / toStr / atDayStart are above hooks)

  const today = dayjs()
    .startOf('day')
    .toDate();
  const maxDate = dayjs()
    .add(maxDaysSearch, 'day')
    .toDate();

  /** Suggested return date: departure + 3 days, capped at maxDate. */
  const suggestedReturnFrom = start => {
    let end = dayjs(start).add(3, 'day').startOf('day').toDate();
    if (dayjs(end).isAfter(dayjs(maxDate).startOf('day'))) {
      end = dayjs(maxDate).startOf('day').toDate();
    }
    return end;
  };

  const suggestedReturnDate = suggestedReturnFrom(today);

  // ─── Display value for the trigger ────────────────────────────────────────

  const showCompact = !departureDate && !compactUsedRef.current;

  // ─── Open / close modal ────────────────────────────────────────────────────

  const openModal = forceTab => {
    if (onFocus) onFocus();
    const tab = forceTab || deriveTab();
    let start = null;
    let end = null;

    if (departureDate === openTicketText) {
      // Fresh range: clear any concrete return so the user picks outbound then return.
      if (returnDate && returnDate !== openTicketText) {
        onReturnChange('');
      }
    } else if (departureDate) {
      start = toDateObj(departureDate);
      if (returnDate && returnDate !== openTicketText) {
        end = toDateObj(returnDate);
      }
    } else if (returnDate && returnDate !== openTicketText) {
      end = toDateObj(returnDate);
    }

    setModalTab(tab);
    setStartDate(atDayStart(start));
    setEndDate(atDayStart(end));
    setCurrentMonth(atDayStart(start) || today);
    monthNavigationUsedRef.current = false;
    tabChangesCountRef.current = 0;
    setIsOpen(true);
    // Hides the chatbot in CDS. TODO: Find a way to show the modal above all the content
    document.querySelector('.bot-cta')?.style.setProperty('display', 'none');
  };

  const closeModal = () => {  
    setIsOpen(false);
    setCurrentMonth(null);
    // Hides the chatbot in CDS. TODO: Find a way to show the modal above all the content
    document.querySelector('.bot-cta')?.style.setProperty('display', 'block');
  };

  const handleInputClick = fromReturn => {
    // Departure field: keep deriveTab() (one-way vs round vs open from current trip).
    // Return field: always open on the Round tab so the calendar is range mode for return.
    openModal(fromReturn ? 'round' : null);
  };

  // ─── Compact date quick-selects ────────────────────────────────────────────

  const handleQuickDate = daysOffset => {
    mixpanelTracker.trackEvent(CALENDAR_EVENT, {
      type: daysOffset === 0 ? 'today' : 'tomorrow',
      calendar_variant: 'new',
    });
    const date = dayjs()
      .add(daysOffset, 'day')
      .toDate();
    compactUsedRef.current = true;
    if (returnDate) onReturnChange('');
    onTripTypeChange('oneWay');
    onDepartureChange(toStr(date));
  };

  const handleMundialClick = e => {
    e?.stopPropagation?.();
    mixpanelTracker.trackEvent(CALENDAR_EVENT, { type: 'mundial', calendar_variant: 'new' });
    // Keep compact row visible (like calendar "Select") until a date is committed — avoids empty inputs.
    openModal('oneWay');
    setCurrentMonth(new Date(2026, 5, 1)); // June 2026
  };

  const handleCompactDateClick = e => {
    // Desktop: Modal renders inside this wrapper; clicks (e.g. Continue) bubble here and
    // would otherwise call openModal() right after closeModal() in the same tick.
    if (e?.target?.closest?.('#modalOverlay')) {
      return;
    }
    // Departure/return rows use their own onClick → openModal(forceTab). If we open again
    // here, openModal() uses deriveTab() and overwrites e.g. Return → Round back to One-way.
    if (
      e?.target?.closest?.('.departureDate-wrapper') ||
      e?.target?.closest?.('.returnDate-wrapper')
    ) {
      return;
    }
    mixpanelTracker.trackEvent(CALENDAR_EVENT, { type: 'choose', calendar_variant: 'new' });
    openModal();
  };

  // ─── Open ticket handlers (defined before handleTabChange to avoid TDZ) ────

  const handleOpenDepartureTicket = () => {
    mixpanelTracker.trackEvent('Open Ticket Selected', { type: 'departure' });
    compactUsedRef.current = true;
    if (returnDate !== openTicketText) {
      onReturnChange('');
    }
    onTripTypeChange('openTicket');
    onDepartureChange(openTicketText);
    if (onDepartureOpenTicket) onDepartureOpenTicket();
  };

  const handleOpenReturnTicket = () => {
    mixpanelTracker.trackEvent('Open Ticket Selected', { type: 'return' });
    compactUsedRef.current = true;
    onTripTypeChange('round');
    if (startDate) onDepartureChange(toStr(startDate));
    onReturnChange(openTicketText);
    if (onReturnOpenTicket) onReturnOpenTicket();
  };

  const handleToggleOpenReturn = () => {
    mixpanelTracker.trackEvent('Open Return Ticket Toggled', {
      enabled: !returnIsOpen,
      calendar_variant: 'new',
    });
    if (returnIsOpen) {
      onReturnChange('');
      onTripTypeChange(startDate ? 'round' : 'oneWay');
    } else {
      handleOpenReturnTicket();
    }
  };

  // ─── Tab change inside modal ───────────────────────────────────────────────

  const handleTabChange = tab => {
    tabChangesCountRef.current += 1;
    mixpanelTracker.trackEvent('Search Calendar Tab Clicked', {
      section: TAB_TO_SECTION[tab],
      calendar_variant: 'new',
    });
    if (tab === 'openTicket') {
      setModalTab(tab);
      handleOpenDepartureTicket();
      return;
    }
    setModalTab(tab);

    if (tab === 'oneWay') {
      onTripTypeChange('oneWay');
      if (departureDate === openTicketText) {
        compactUsedRef.current = true;
        onDepartureChange(toStr(today));
        setCurrentMonth(today);
        setStartDate(atDayStart(today));
        setEndDate(null);
        if (returnDate) onReturnChange('');
        return;
      }

      const outbound =
        departureDate && departureDate !== openTicketText
          ? atDayStart(startDate || toDateObj(departureDate))
          : null;

      if (outbound && returnDate && returnDate !== openTicketText) {
        compactUsedRef.current = true;
        onDepartureChange(toStr(outbound));
        onReturnChange('');
        setStartDate(outbound);
        setEndDate(null);
        setCurrentMonth(outbound);
        return;
      }

      if (outbound) {
        if (returnDate) onReturnChange('');
        setStartDate(outbound);
        setEndDate(null);
        setCurrentMonth(outbound);
      } else {
        setStartDate(null);
        setEndDate(null);
      }
      return;
    }

    if (tab === 'round') {
      onTripTypeChange('round');
      const hasConcreteDeparture = Boolean(departureDate) && departureDate !== openTicketText;
      const hasConcreteReturn = Boolean(returnDate) && returnDate !== openTicketText;

      if (hasConcreteDeparture && hasConcreteReturn) {
        const s = atDayStart(toDateObj(departureDate));
        const e = atDayStart(toDateObj(returnDate));
        if (s && e) {
          setStartDate(s);
          setEndDate(e);
          setCurrentMonth(s);
        }
        return;
      }

      const start =
        atDayStart(startDate || (hasConcreteDeparture ? toDateObj(departureDate) : null)) || today;
      const end = suggestedReturnFrom(start);

      compactUsedRef.current = true;
      onDepartureChange(toStr(start));
      onReturnChange(toStr(end));
      setStartDate(start);
      setEndDate(end);
      setCurrentMonth(start);
      return;
    }
  };

  // ─── Calendar date selection handlers ─────────────────────────────────────

  const isDesktop = getProductType() !== 'web-mobile';

  const handleRoundOpenSlotChange = date => {
    if (!date) return;
    const hadReturnOpen = returnDate === openTicketText;
    mixpanelTracker.trackEvent(CALENDAR_EVENT, {
      type: 'personalized',
      tab: 'round',
      calendar_variant: 'new',
    });
    compactUsedRef.current = true;
    onTripTypeChange('round');

    // Return was "open ticket": clear it and go back to range mode (first day = start of range).
    if (hadReturnOpen) {
      onReturnChange('');
      onDepartureChange(toStr(date));
      setStartDate(date);
      setEndDate(null);
      return;
    }

    onDepartureChange(toStr(date));
    if (isDesktop) {
      closeModal();
    }
  };

  const handleSaveRangeDates = (start, end) => {
    compactUsedRef.current = true;
    onTripTypeChange('round');
    onDepartureChange(toStr(start));
    onReturnChange(toStr(end));
  };

  // react-day-picker range: onSelect receives { from, to } | undefined.
  // First click usually has `to` unset (outbound only). A second click on the same day
  // sends `from` and `to` equal (addToRange with min 0) — that must become a same-day
  // round trip even on the One-way tab, so we always keep `to` when the picker sends it.
  const handleRangeChange = range => {
    if (!range) {
      // With resetOnSelect, a third click on a same-day range emits undefined (library
      // "clear"). We keep the current selection — users clear dates via the clean control.
      if (startDate != null || endDate != null) {
        return;
      }
      setStartDate(null);
      setEndDate(null);
      return;
    }
    const start = atDayStart(range?.from || null);
    const rawEnd = atDayStart(range?.to || null);
    const end = rawEnd && start ? rawEnd : null;
    setStartDate(start);
    setEndDate(end);
    if (start && end) {
      setModalTab('round');
      mixpanelTracker.trackEvent(CALENDAR_EVENT, {
        type: 'personalized',
        tab: 'round',
        calendar_variant: 'new',
        start: start.toISOString(),
        end: end.toISOString(),
        difference: differenceInDays(end, start),
      });
      handleSaveRangeDates(start, end);
    } else if (start && !end) {
      // Outbound only (incomplete range) → One-way tab until the user picks the return day (then Round).
      setModalTab('oneWay');
      mixpanelTracker.trackEvent(CALENDAR_EVENT, {
        type: 'personalized',
        tab: 'oneWay',
        calendar_variant: 'new',
      });
      compactUsedRef.current = true;
      onTripTypeChange('oneWay');
      if (returnDate) onReturnChange('');
      onDepartureChange(toStr(start));
    }
  };

  const handleTodayClick = () => {
    mixpanelTracker.trackEvent('Calendar Today Clicked', { calendar_variant: 'new' });
    compactUsedRef.current = true;
    onTripTypeChange('oneWay');
    if (returnDate) onReturnChange('');
    onDepartureChange(toStr(today));
    setStartDate(today);
    setEndDate(null);
    setModalTab('oneWay');
  };

  const handleContinue = () => {
    const eventProps = {
      tab: TAB_TO_SECTION[modalTab],
      is_open_departure: departureDate === openTicketText,
      is_open_return: returnDate === openTicketText,
      used_month_navigation: monthNavigationUsedRef.current,
      tab_changes_count: tabChangesCountRef.current,
      calendar_variant: 'new',
    };
    mixpanelTracker.trackEvent(CALENDAR_CONTINUE_EVENT, eventProps);

    closeModal();

    // One-way: no outbound date picked → default to today on Continue.
    if (modalTab === 'oneWay' && !departureDate) {
      if (startDate) {
        pendingContinueRef.current = true;
        onDepartureChange(toStr(startDate));
        return;
      }
      compactUsedRef.current = true;
      onTripTypeChange('oneWay');
      if (returnDate) onReturnChange('');
      onDepartureChange(toStr(today));
      setStartDate(today);
      setEndDate(null);
      pendingContinueRef.current = true;
      return;
    }

    // Round trip: nothing saved yet → defaults or partial range from calendar.
    if (!departureDate && modalTab === 'round' && !returnDate) {
      compactUsedRef.current = true;
      onTripTypeChange('round');
      if (startDate && endDate) {
        handleSaveRangeDates(startDate, endDate);
      } else if (startDate) {
        handleSaveRangeDates(startDate, suggestedReturnFrom(startDate));
      } else {
        handleSaveRangeDates(today, suggestedReturnDate);
      }
      pendingContinueRef.current = true;
      return;
    }

    if (onContinue) onContinue();
  };

  // ─── Clean buttons ─────────────────────────────────────────────────────────

  const handleCleanDeparture = e => {
    e.stopPropagation();
    compactUsedRef.current = false;
    onDepartureChange('');
    if (returnDate) onReturnChange('');
    setStartDate(null);
    setEndDate(null);
    setModalTab('oneWay');
    onTripTypeChange('oneWay');
    closeModal();
  };

  // ─── Continue footer date display ─────────────────────────────────────────

  const fmtDate = date => dayjs(date).format('D MMM');

  // True while the user has clicked the first day of a new range but not the second yet
  const isSelectingRange = startDate !== null && endDate === null;

  const footerDeparture = (() => {
    if (modalTab === 'openTicket') return openTicketText;
    if (departureDate === openTicketText) return openTicketText;
    if (startDate) return fmtDate(startDate);
    if (departureDate) return fmtDate(toDateObj(departureDate)) || departureDate;
    return null;
  })();

  const footerReturn = (() => {
    if (returnDate === openTicketText) return openTicketText;
    if (endDate) return fmtDate(endDate);
    // Hide the previous return date while the user is picking a new range start
    if (isSelectingRange) return null;
    if (returnDate) return fmtDate(toDateObj(returnDate)) || returnDate;
    return null;
  })();

  const shouldShowReturnNotSelected =
    returnDate !== openTicketText &&
    !endDate &&
    (isSelectingRange || !returnDate);

  const shouldShowDepartureNotSelected =
    departureDate !== openTicketText && !startDate && !departureDate;

  const showMobileFooterDates =
    modalTab === 'openTicket' ||
    footerDeparture ||
    shouldShowDepartureNotSelected ||
    footerReturn ||
    shouldShowReturnNotSelected;

  // Single-select only when return is open and departure is concrete (pick outbound).
  // Same on One-way and Round tabs now that both always use the range calendar until this slot mode.
  // Open departure uses the normal range picker so the user sets both legs from scratch.
  const departureIsOpen = departureDate === openTicketText;
  const returnIsOpen = returnDate === openTicketText;
  const roundWithOpenSlot =
    (modalTab === 'oneWay' || modalTab === 'round') && returnIsOpen && !departureIsOpen;

  // ─── Badge text for the custom DayButton ──────────────────────────────────
  const dayBadgeText = roundWithOpenSlot && startDate ? departureBadge : null;

  // Today keeps accent + icon unless today is the range start or end.
  // Still applies with a full range or when today is inside the range.
  const startTouchesToday = startDate && dayjs(startDate).isSame(dayjs(today), 'day');
  const endTouchesToday = endDate && dayjs(endDate).isSame(dayjs(today), 'day');
  const showTodayAccentHint =
    modalTab !== 'openTicket' && !startTouchesToday && !endTouchesToday;

  // ─── Shared DayPicker props ────────────────────────────────────────────────

  const sharedDayPickerProps = {
    weekStartsOn: 1,
    disabled: [{ before: today }, { after: maxDate }],
    startMonth: today,
    endMonth: maxDate,
    month: currentMonth || today,
    onMonthChange: month => {
      const prevMonth = currentMonth || today;
      monthNavigationUsedRef.current = true;
      mixpanelTracker.trackEvent('Calendar Month Changed', {
        month: dayjs(month).month() + 1,
        action: dayjs(month).isAfter(dayjs(prevMonth), 'month') ? 'next' : 'prev',
        month_delta: dayjs(month).diff(dayjs().startOf('month'), 'month'),
        tab: TAB_TO_SECTION[modalTab],
        calendar_variant: 'new',
      });
      setCurrentMonth(month);
    },
    formatters: {
      formatCaption: date => dayjs(date).format('MMMM YYYY'),
      formatWeekdayName: date => dayjs(date).format('ddd'),
    },
    components: {
      MonthCaption,
      Nav: HiddenNav,
    },
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={wrapperRef}
      className={`form-field-date-wrap udp-wrapper${error ? ' udp-error' : ''} udp-trigger-row${
        showCompact ? ' udp-trigger-row--compact-controls' : ''
      }${isOpen ? ' udp-trigger-row--focused' : ''}`}
      onClick={handleCompactDateClick}
    >
      {/* ── Compact DatesControls (no date selected yet) ─────────────────── */}
      {showCompact && (
        <div className="udp-compact-dates-controls">
          <DatesControls
            label={<Text id="label.when_traveling" />}
            locale={locale}
            onDateClick={handleCompactDateClick}
            onTodayClick={() => handleQuickDate(0)}
            onTomorrowClick={() => handleQuickDate(1)}
            onMundialClick={handleMundialClick}
            showMundialButton={showMundialButton}
            showTomorrowWithMundial={showTomorrowWithMundial}
          />
        </div>
      )}

      {/* ── Value trigger (date is already selected) ──────────────────────── */}
      {!showCompact && (
        <Fragment>
          {/* Departure field */}
          <div
            className={`departureDate-wrapper${error ? ' field-error' : ''}`}
            onClick={() => handleInputClick(false)}
          >
            <label className={`label label-dates${error ? ' label-error' : ''}`}>
              <Text id="label.departure_date" />
            </label>

            <input
              type="text"
              readOnly
              className="udp-display-input"
              value={(departureDate === openTicketText ? openTicketLabel : departureDate) || ''}
              placeholder=" "
            />
          </div>

          {/* Return field — shown whenever there is a departure or return date */}
          {(departureDate || returnDate) && (
            <>
              <div className="returnDate-wrapper" onClick={() => handleInputClick(true)}>
                <label className="label label-dates">
                  <Text id="label.return_date" />
                </label>

                <input
                  type="text"
                  readOnly
                  className="udp-display-input"
                  value={(returnDate === openTicketText ? openTicketLabel : returnDate) || ''}
                  placeholder={returnPlaceholder}
                />
              </div>
            </>
          )}

          {/* Single clear button — clears both dates */}
          {(departureDate || returnDate) && (
            <div
              className="clean-place-input udp-clear-btn"
              onClick={handleCleanDeparture}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: selectIcon('cleanIcon', false) }}
            />
          )}

          {!departureDate && !returnDate && (
            <div
              className="iqdropdown-selection"
              onClick={() => handleInputClick(false)}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: selectIcon('arrow', false) }}
            />
          )}
        </Fragment>
      )}

      {/* ── Modal with tab buttons + calendar ────────────────────────────── */}
      {isOpen && (
        <Modal
          onClose={closeModal}
          title={<Text id="title.select_trip_type" />}
          contentClassName="udp-modal-content"
          modalClassName="udp-modal-unified"
          isFitContent
          showCloseButton
          footer={
            <div className="udp-continue-footer">
              {showMobileFooterDates && (
                <div className="udp-continue-dates udp-continue-dates--mobile-only">
                  <p className="udp-continue-row">
                    <span className="udp-continue-label">
                      <Text id="label.departure_date" />
                    </span>{' '}
                    <span className="udp-continue-value">
                      {shouldShowDepartureNotSelected ? (
                        <span className="udp-continue-not-selected">
                          <Text id="label.departure_not_selected" />
                        </span>
                      ) : (
                        footerDeparture
                      )}
                    </span>
                  </p>
                  <p className="udp-continue-row">
                    <span className="udp-continue-label">
                      <Text id="label.return_date" />
                    </span>{' '}
                    <span className="udp-continue-value">
                      {shouldShowReturnNotSelected ? (
                        <span className="udp-continue-not-selected">
                          <Text id="label.return_not_selected" />
                        </span>
                      ) : (
                        footerReturn || returnPlaceholder
                      )}
                    </span>
                  </p>
                </div>
              )}
              <button
                type="button"
                className="udp-continue-btn"
                onClick={handleContinue}
                style={{ backgroundColor: buttonBg }}
              >
                <Text id={hasPassengersModal ? 'button.continue' : 'button.search'} />
              </button>
            </div>
          }
        >
          {/* Sentinel: when scrolled out of view the tabs are sticky */}
          {/* Tab selector */}
          <div className="udp-tabs">
            <button
              type="button"
              className={`udp-tab${modalTab === 'oneWay' ? ' active' : ''}`}
              onClick={() => handleTabChange('oneWay')}
            >
              <Text id="label.unified_one_way" />
            </button>

            <button
              type="button"
              className={`udp-tab${modalTab === 'round' ? ' active' : ''}`}
              onClick={() => handleTabChange('round')}
            >
              <Text id="label.unified_round" />
            </button>

            {showOpenTicket && (
              <button
                type="button"
                className={`udp-tab${modalTab === 'openTicket' ? ' active' : ''}`}
                onClick={() => handleTabChange('openTicket')}
              >
                <Text id="label.unified_open" />
              </button>
            )}
          </div>

          {/* Inline calendar – shared for Simple and Round */}
          {modalTab !== 'openTicket' && (
            <div className="udp-calendar-wrap">
              <DayPicker
                {...sharedDayPickerProps}
                today={today}
                components={{
                  ...sharedDayPickerProps.components,
                  DayButton: props => (
                    <DayButtonWithBadge
                      {...props}
                      badgeText={dayBadgeText}
                      showTodayAccentHint={showTodayAccentHint}
                    />
                  ),
                }}
                mode={!roundWithOpenSlot ? 'range' : 'single'}
                min={!roundWithOpenSlot ? 0 : undefined}
                selected={
                  !roundWithOpenSlot
                    ? startDate || endDate
                      ? endDate
                        ? { from: startDate, to: endDate }
                        : { from: startDate }
                      : undefined
                    : startDate || undefined
                }
                onSelect={roundWithOpenSlot ? handleRoundOpenSlotChange : handleRangeChange}
                resetOnSelect
              />

              <div className="udp-footer">
                <button
                  type="button"
                  className="udp-footer-btn udp-footer-btn--today"
                  onClick={handleTodayClick}
                >
                  <Text id="label.today" />
                </button>
                <button
                  type="button"
                  className="udp-footer-btn"
                  onClick={() => {
                    mixpanelTracker.trackEvent('Calendar Close Clicked', {
                      calendar_variant: 'new',
                    });
                    closeModal();
                  }}
                >
                  <Text id="button.close" />
                </button>
              </div>

              {showOpenTicket && (startDate || departureIsOpen) && (
                <ReturnOpenSwitch
                  returnIsOpen={returnIsOpen}
                  handleToggleOpenReturn={handleToggleOpenReturn}
                  flatVariant={flatVariant}
                />
              )}
            </div>
          )}

          {modalTab === 'openTicket' && (
            <div className="udp-open-ticket">
              <div className="udp-open-ticket__content">
                <div className="udp-open-ticket__text">
                  <div className="udp-open-ticket__header">
                    <span className="udp-open-ticket__title">
                      <Text id="label.open_ticket_title" />
                    </span>
                    <img
                      src={flatVariant ? flexibleCalendarFlatImg : flexibleCalendarImg}
                      className="udp-open-ticket__img"
                      alt=""
                      aria-hidden="true"
                    />
                  </div>
                  <div className="udp-open-ticket__divider" />
                  <div className="udp-open-ticket__description">
                    <span className="udp-open-ticket__subtitle">
                      <Text id="label.open_ticket_subtitle" />
                    </span>
                    <span className="udp-open-ticket__subtitle">
                      <Text id="label.open_ticket_redeem" />
                    </span>
                  </div>
                </div>
              </div>
              <ReturnOpenSwitch
                returnIsOpen={returnIsOpen}
                handleToggleOpenReturn={handleToggleOpenReturn}
                flatVariant={flatVariant}
              />
            </div>
          )}
        </Modal>
      )}
      {error && (
        <div
          className="icon-error"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: selectIcon('error', false) }}
        />
      )}
    </div>
  );
};

export default withText(() => ({
  returnPlaceholder: <Text id="placeholder.return_option" />,
  departureBadge: <Text id="label.departure_badge" />,
  openTicketLabel: <Text id="label.open_ticket_short" />,
}))(UnifiedDatePicker);
